import { useEffect, useState } from "react";
import { api } from "../../App";
import { Button } from "@/components/ui/button";
import { Phone, PhoneCall } from "lucide-react";
import { LogCallDialog } from "./LogCallDialog";

const TONE = { success: "bg-green-100 text-green-800", follow_up: "bg-amber-100 text-amber-800", no_answer: "bg-gray-200 text-gray-700" };
const fmt = (iso) => new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" });

export const lastCallLabel = (iso) => {
  if (!iso) return null;
  const d = new Date(iso), t = new Date();
  const days = Math.round((new Date(t.getFullYear(), t.getMonth(), t.getDate()) - new Date(d.getFullYear(), d.getMonth(), d.getDate())) / 864e5);
  return days <= 0 ? "Today" : days === 1 ? "Yesterday" : days < 30 ? `${days} days ago` : new Date(iso).toLocaleDateString();
};

export const LastCallCell = ({ iso }) => (
  <span className={`text-md3-body-small whitespace-nowrap ${iso ? "" : "text-gray-400 text-xs"}`} title={iso ? fmt(iso) : ""}>
    {lastCallLabel(iso) || "Never"}
    {isStaleCall(iso) && <span className="ml-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-red-100 text-red-700" data-testid="stale-call-tag">30+ days</span>}
  </span>
);

export const LogCallButton = ({ entityType, entityId, name, onLogged }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button size="sm" variant="ghost" title="Log Call" onClick={() => setOpen(true)} data-testid={`log-call-btn-${entityId}`}>
        <Phone className="h-4 w-4" />
      </Button>
      {open && <LogCallDialog open onClose={() => setOpen(false)} preset={{ entity_type: entityType, entity_id: entityId, name }} onLogged={onLogged} />}
    </>
  );
};

export const CallHistory = ({ entityType, entityId, name, onLogged }) => {
  const [logs, setLogs] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => api.get(`/call-logs?entity_type=${entityType}&entity_id=${entityId}`).then((r) => setLogs(r.data)).catch(() => setLogs([]));
  useEffect(() => { if (entityId) load(); }, [entityId]);
  return (
    <div className="space-y-3 pt-4 border-t" data-testid="call-history">
      <div className="flex items-center justify-between">
        <h3 className="text-md3-title-medium font-semibold flex items-center gap-2"><PhoneCall className="h-4 w-4" /> Call History {logs && <span className="text-sm text-gray-500 font-normal">({logs.length})</span>}</h3>
        <Button size="sm" variant="outline" className="rounded-md3-full" onClick={() => setOpen(true)} data-testid="call-history-log-btn"><Phone className="h-4 w-4 mr-1" /> Log Call</Button>
      </div>
      {logs === null ? <p className="text-sm text-gray-500">Loading...</p> : logs.length === 0 ? (
        <p className="text-sm text-gray-500" data-testid="call-history-empty">No calls logged yet.</p>
      ) : (
        <div className="max-h-72 overflow-y-auto space-y-2 pr-1">
          {logs.map((l) => (
            <div key={l.id} className="rounded-md border border-gray-200 bg-gray-50 p-3" data-testid={`call-log-${l.id}`}>
              <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600">
                <span className={`px-2 py-0.5 rounded font-medium ${TONE[l.outcome] || ""}`}>{l.outcome_label}</span>
                <span className="font-medium text-gray-800">{fmt(l.call_at)}</span>
                {l.duration_min != null && <span>{l.duration_min} min</span>}
                {l.contact_name && <span>· Spoke to {l.contact_name}</span>}
                <span className="ml-auto">by {l.created_by_name}</span>
              </div>
              <p className="text-sm text-gray-800 mt-1.5 whitespace-pre-wrap">{l.notes}</p>
            </div>
          ))}
        </div>
      )}
      {open && <LogCallDialog open onClose={() => setOpen(false)} preset={{ entity_type: entityType, entity_id: entityId, name }} onLogged={(log) => { load(); onLogged?.(log); }} />}
    </div>
  );
};

export const QuickLogCallButton = ({ onLogged, className = "" }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="outline" className={`rounded-md3-full ${className}`} onClick={() => setOpen(true)} data-testid="quick-log-call-btn">
        <PhoneCall className="mr-2 h-4 w-4" /> Log Call
      </Button>
      {open && <LogCallDialog open onClose={() => setOpen(false)} onLogged={onLogged} />}
    </>
  );
};

// Keeps a list's last_call_at in sync after a call is logged (no refetch needed)
export const withLastCall = (list, log) =>
  list.map((x) => (x.id === log.entity_id && (!x.last_call_at || x.last_call_at < log.call_at) ? { ...x, last_call_at: log.call_at } : x));

export const isStaleCall = (iso) => !!iso && Date.now() - new Date(iso).getTime() > 30 * 864e5;
